import { LEGAL_URLS } from '../lib/consent';
import { useConsent } from '../context/ConsentContext';

/**
 * Bottom-right cookie / privacy notice for the admin console.
 *
 * The admin console has no user-toggleable telemetry categories (see
 * `lib/consent.ts` for the reasoning), so this card is a plain
 * acknowledgment notice — a single "Got it" button plus links to the
 * platform legal pages. Users can re-open it any time from the sidebar
 * "Privacy notice" link, which forces `isSettingsOpen` to true.
 */
export function PrivacyChoicesCard() {
  const {
    ready,
    hasAcknowledged,
    isSettingsOpen,
    acknowledge,
    closeSettings,
  } = useConsent();

  if (!ready) return null;
  const shouldShow = !hasAcknowledged || isSettingsOpen;
  if (!shouldShow) return null;

  // When the card is opened after an existing acknowledgment (sidebar
  // link), a plain close is enough — we don't need to re-log a duplicate
  // event. Before the first acknowledgment, the only exit is "Got it".
  const page = hasAcknowledged ? 'sidebar' : 'banner';

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie notice"
      className="fixed z-9999 bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm"
    >
      <div className="rounded-xl shadow-2xl border border-border bg-white p-4 sm:p-5 text-sm text-foreground">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="font-semibold text-base">Cookies &amp; privacy</div>
          {hasAcknowledged && (
            <button
              type="button"
              onClick={closeSettings}
              aria-label="Close"
              className="rounded-md -mt-1 -mr-1 p-1 text-muted-foreground hover:text-foreground"
            >
              &times;
            </button>
          )}
        </div>

        <p className="leading-relaxed mb-4 text-muted-foreground">
          Artist Intro Console uses strictly-necessary cookies to keep you
          signed in and remember your workspace preferences, and it uses
          professional & secure tools to monitor errors and performance so we can keep the
          service reliable. No advertising or third-party analytics. See our{' '}
          <a
            href={LEGAL_URLS.privacy}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-foreground"
          >
            Privacy Policy
          </a>
          ,{' '}
          <a
            href={LEGAL_URLS.cookies}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-foreground"
          >
            Cookie Policy
          </a>
          , and{' '}
          <a
            href={LEGAL_URLS.terms}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-foreground"
          >
            Terms of Service
          </a>
          .
        </p>

        <div className="flex">
          <button
            type="button"
            onClick={() => acknowledge(page)}
            className="w-full rounded-md px-3 py-2 text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
